import { ProductJourney } from "@shared/analytics/productEvents";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import { API_URLS, type RetailWalletChallengeResponse, type YaasWalletResponse } from "@solomon-apps/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { observeSettingsVerification } from "@/services/analytics/settings";
import { normalizeVerificationTransfer } from "@/services/yaas/verificationTransfer";
import { WalletVerificationError } from "@/services/yaas/walletVerificationError";

import { yaasWalletQueryKey } from "./queryKeys";
import { useWalletVerification, type VerificationProof } from "./useWalletVerification";
import { requestWalletActionChallenge, submitWithVerificationRetry } from "./verificationRequests";
import { mergeWalletViews } from "./walletView";

export type ApiDistributionMode = "stream" | "claim";

export interface SetDistributionModeInput {
    walletAddress: string;
    analytics?: ProductJourney;
    distributionMode: ApiDistributionMode;
    preferredMethod?: "microTransfer";
}

export type SetDistributionModeResult =
    | { success: true; data: YaasWalletResponse }
    | { success: false; error: string | null; canRetryWithMicroTransfer: boolean };

export interface UseDistributionModeResult {
    /** Account-level payout-mode update, via challenge → sign → submit.
     *  Account-scoped (not per-position): the mode lives on the
     *  participant account in yaas-api. */
    setDistributionMode: (input: SetDistributionModeInput) => Promise<SetDistributionModeResult>;
    isLoading: boolean;
    error: string | null;
}

export function distributionModeUrl(apiBase: string, walletAddress: string): string {
    return `${apiBase}/api/yaas/wallet/${walletAddress}/distribution-mode`;
}

export function distributionModeChallengeUrl(apiBase: string, walletAddress: string): string {
    return `${distributionModeUrl(apiBase, walletAddress)}/challenge`;
}

export function distributionModeChallengeBody(distributionMode: ApiDistributionMode): Record<string, unknown> {
    return { distributionMode };
}

export function distributionModeSubmitBody(
    distributionMode: ApiDistributionMode,
    nonce: string,
    proof: VerificationProof
): Record<string, unknown> {
    return { distributionMode, nonce, proof };
}

export function distributionModeErrorMessage(error: unknown): string | null {
    if (error instanceof WalletVerificationError && error.code === "wallet_rejected") return null;
    return error instanceof Error ? error.message : "Failed to update distribution mode";
}

async function requestDistributionModeChallenge(input: {
    walletAddress: string;
    analytics?: ProductJourney;
    distributionMode: ApiDistributionMode;
}): Promise<RetailWalletChallengeResponse> {
    return requestWalletActionChallenge(
        distributionModeChallengeUrl(API_URLS.YAAS, input.walletAddress),
        distributionModeChallengeBody(input.distributionMode),
        "Failed to request distribution mode challenge"
    );
}

async function submitDistributionMode(input: {
    walletAddress: string;
    analytics?: ProductJourney;
    distributionMode: ApiDistributionMode;
    nonce: string;
    proof: VerificationProof;
    expiresAt: string;
}): Promise<YaasWalletResponse> {
    const response = await submitWithVerificationRetry(
        distributionModeUrl(API_URLS.YAAS, input.walletAddress),
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(distributionModeSubmitBody(input.distributionMode, input.nonce, input.proof))
        },
        input.expiresAt
    );
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || body.message || "Failed to update distribution mode");
    }
    return response.json() as Promise<YaasWalletResponse>;
}

export function useDistributionMode(): UseDistributionModeResult {
    const walletVerification = useWalletVerification();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const setDistributionMode = async (input: SetDistributionModeInput): Promise<SetDistributionModeResult> => {
        const analytics =
            input.analytics ?? new ProductJourney("rewards_distribution", "rewards_footer", input.walletAddress);
        const attempt = analytics.attempt({ distribution_mode: input.distributionMode });
        attempt.send("challenge_started");
        setIsLoading(true);
        setError(null);
        let challenge: RetailWalletChallengeResponse | null = null;
        try {
            challenge = await requestDistributionModeChallenge(input);
            attempt.challengeCompleted();
            const proof = await walletVerification.verify({
                purpose: "action",
                analytics: observeSettingsVerification(attempt),
                walletAddress: input.walletAddress,
                message: challenge.message,
                nonce: challenge.nonce,
                expiresAt: challenge.expiresAt,
                microTransfer: challenge.microTransfer,
                preferredMethod: input.preferredMethod
            });
            attempt.submit(proof.method);
            const data = await submitDistributionMode({
                ...input,
                nonce: challenge.nonce,
                proof,
                expiresAt: challenge.expiresAt
            });
            // Apply fresh settings while retaining prior evidence if discovery is unavailable.
            attempt.complete();
            queryClient.setQueryData<YaasWalletResponse>(yaasWalletQueryKey(input.walletAddress), (previous) =>
                mergeWalletViews(previous, data)
            );
            return { success: true, data };
        } catch (err: unknown) {
            attempt.fail(err);
            const message = distributionModeErrorMessage(err);
            setError(message);
            const canRetryWithMicroTransfer =
                err instanceof WalletVerificationError &&
                err.method === "signMessage" &&
                err.code === "wallet_rejected" &&
                FEATURE_FLAGS.retailMicroTransferVerification &&
                challenge !== null &&
                normalizeVerificationTransfer(challenge.microTransfer, challenge.nonce) !== null;
            return { success: false, error: message, canRetryWithMicroTransfer };
        } finally {
            setIsLoading(false);
        }
    };

    return { setDistributionMode, isLoading, error };
}
